/**
 * The evaluation case's eight scenario domains, in order (INS-12810).
 *
 * The data's single source is the shared enum `enums/evaluation_case_scenario_domains.json`,
 * which the backend registry `EvaluationCase::ScenarioDomains` reads too. It is written out here
 * rather than imported because importing JSON widens `'taxes'` to `string`, and
 * `ScenarioDomainKey` must stay a literal union. The Vitest parity test holds this list to the
 * shared file, so editing one without the other fails.
 *
 * Keys for the five shape-carrying domains are the `evaluation_case_domain_enum` spellings.
 */
export const SCENARIO_DOMAINS = [
  {
    key: 'price_deck',
    label: 'Price Deck',
    column: 'price_deck_id',
    scenarioType: 'PriceDeck',
    shapeCarrying: false,
    gatesCoverage: false,
    queryPath: '/queries/price_decks',
    scenarioPath: 'price_decks',
  },
  {
    key: 'development_pace',
    label: 'Development Pace',
    column: 'development_pace_scenario_id',
    scenarioType: 'DevelopmentPaceScenario',
    shapeCarrying: true,
    gatesCoverage: true,
    queryPath: '/queries/development_pace_scenarios',
    scenarioPath: 'development_pace_scenarios',
  },
  {
    key: 'type_curve',
    label: 'Type Curve',
    column: 'type_curve_scenario_id',
    scenarioType: 'TypeCurveScenario',
    shapeCarrying: false,
    gatesCoverage: true,
    queryPath: '/queries/reservoir/type_curve_scenarios',
    scenarioPath: 'type_curve_scenarios',
  },
  {
    key: 'dca',
    label: 'DCA',
    column: 'dca_scenario_id',
    scenarioType: 'DcaScenario',
    shapeCarrying: false,
    gatesCoverage: false,
    queryPath: '/queries/reservoir/dca_scenarios',
    scenarioPath: 'dca_scenarios',
  },
  {
    key: 'taxes',
    label: 'Taxes',
    column: 'tax_scenario_id',
    scenarioType: 'TaxScenario',
    shapeCarrying: true,
    gatesCoverage: true,
    queryPath: '/queries/tax_scenarios',
    scenarioPath: 'tax_scenarios',
  },
  {
    key: 'expenses',
    label: 'Expenses',
    column: 'expense_scenario_id',
    scenarioType: 'ExpenseScenario',
    shapeCarrying: true,
    gatesCoverage: true,
    queryPath: '/queries/expense_scenarios',
    scenarioPath: 'expense_scenarios',
  },
  {
    key: 'differentials',
    label: 'Differentials',
    column: 'differential_scenario_id',
    scenarioType: 'DifferentialScenario',
    shapeCarrying: true,
    gatesCoverage: true,
    queryPath: '/queries/differential_scenarios',
    scenarioPath: 'differential_scenarios',
  },
  {
    key: 'discount_rates',
    label: 'Discount Rates',
    column: 'discount_rate_scenario_id',
    scenarioType: 'DiscountRateScenario',
    shapeCarrying: true,
    gatesCoverage: true,
    queryPath: '/queries/discount_rate_scenarios',
    scenarioPath: 'discount_rate_scenarios',
  },
] as const;

export type ScenarioDomain = (typeof SCENARIO_DOMAINS)[number];
export type ScenarioDomainKey = ScenarioDomain['key'];
/**
 * The shape-carrying domains (CaseResolver::PER_AREA_DOMAINS): those resolved per area from their
 * scenario's area configs, the ones a run can override per area. The map and coverage key on the
 * wider COVERAGE_DOMAIN_KEYS.
 */
export type ShapeCarryingDomainKey = Extract<
  ScenarioDomain,
  { shapeCarrying: true }
>['key'];

export const SHAPE_CARRYING_DOMAIN_KEYS = SCENARIO_DOMAINS.filter(
  (domain): domain is Extract<ScenarioDomain, { shapeCarrying: true }> =>
    domain.shapeCarrying
).map((domain) => domain.key);

/**
 * The domains whose scenario decides which slots a run values (EvaluationCase::ScenarioDomains::
 * COVERAGE): the shape-carrying five, and Type Curve, which values a permit, DUC or LTD only where
 * it prices one. The case map draws a footprint for each and intersects them all. DCA and Price
 * Deck gate nothing and draw nothing.
 */
export type CoverageDomainKey = Extract<
  ScenarioDomain,
  { gatesCoverage: true }
>['key'];

export const COVERAGE_DOMAIN_KEYS = SCENARIO_DOMAINS.filter(
  (domain): domain is Extract<ScenarioDomain, { gatesCoverage: true }> =>
    domain.gatesCoverage
).map((domain) => domain.key);

/**
 * Whether a registry key the BE sent is a coverage domain.
 *
 * @param {string} key - A domain key.
 * @return {boolean} True for a domain that gates coverage.
 */
export function isCoverageDomainKey(key: string): key is CoverageDomainKey {
  return (COVERAGE_DOMAIN_KEYS as readonly string[]).includes(key);
}

/**
 * Whether a registry key the BE sent is a shape-carrying domain.
 *
 * @param {string} key - A domain key.
 * @return {boolean} True for a shape-carrying domain.
 */
export function isShapeCarryingDomainKey(
  key: string
): key is ShapeCarryingDomainKey {
  return (SHAPE_CARRYING_DOMAIN_KEYS as readonly string[]).includes(key);
}

/**
 * A domain's label for display ("Taxes"), from a registry key the BE sent. A coverage response's
 * pending 'universe' (the package's slots, not a domain) reads as "package slots"; any other key
 * the registry does not know is shown as sent.
 *
 * @param {string} key - A registry key, or 'universe'.
 * @return {string} The label.
 */
export function domainLabel(key: string): string {
  if (key === 'universe') return 'package slots';

  return SCENARIO_DOMAINS.find((domain) => domain.key === key)?.label ?? key;
}

/**
 * Labels for a list of registry keys, joined for a sentence ("Taxes, Expenses").
 *
 * @param {string[]} keys - Registry keys (or 'universe').
 * @return {string} The labels, comma-joined.
 */
export function domainLabels(keys: readonly string[]): string {
  return keys.map(domainLabel).join(', ');
}

/**
 * A scenario's detail page, from the domain it belongs to and its id: the link a run's scenario
 * row and the expense-coverage callout both open.
 *
 * @param {object} scenario - The domain key and scenario id (a `RunScenarioRow` carries both).
 * @param {string} scenario.domain - A registry key.
 * @param {number | null} scenario.scenarioId - The scenario, or null when there is none.
 * @return {string | null} The href, or null with no scenario or an unknown domain.
 */
export function scenarioHref({
  domain,
  scenarioId,
}: {
  domain: string;
  scenarioId: number | null;
}): string | null {
  const entry = SCENARIO_DOMAINS.find((candidate) => candidate.key === domain);
  return entry && scenarioId !== null
    ? `/${entry.scenarioPath}/${scenarioId}`
    : null;
}
